import React, { useEffect, useRef } from 'react';
import { LayoutDashboard, Briefcase, ClipboardList, BarChart3, BarChart2, LineChart, Shield, Settings as SettingsIcon, SlidersHorizontal, Activity, Layers } from 'lucide-react';
import { PageKey } from '../types';

// The nav registry: the pages the shell renders, in nav order. `title` is the
// one-sentence statement of what the page is for -- surfaced as a tooltip here,
// and available as a page subhead later (doc 29, phase 5).
//
// Rendered as a horizontal rail pinned to the bottom of the shell rather than a
// left sidebar: the pattern is adopted from dse-terminal, and it returns the
// sidebar's horizontal track to the content column at every viewport width.
const NAV_ITEMS: { key: PageKey; label: string; title: string; icon: React.ElementType }[] = [
  { key: 'dashboard',   label: 'Dashboard',  title: 'Session overview: connection, phase, quotes, and desk health',          icon: LayoutDashboard },
  { key: 'quotes',      label: 'Quotes',     title: 'Per-counter quote board: our two-sided market, inventory and P&L',     icon: Activity },
  { key: 'analysis',    label: 'Analysis',   title: 'Trading intelligence: OFI, VPIN/toxicity, regime, and ML predictions',  icon: BarChart3 },
  { key: 'positions',   label: 'Portfolio',  title: 'Per-counter inventory, exposure, cash and T+2 settlement',              icon: Briefcase },
  { key: 'orders',      label: 'Orders',     title: 'Working orders, order entry, cancel and replace',                       icon: ClipboardList },
  { key: 'risk',        label: 'Risk',       title: 'Risk gauges, limit utilization, and the risk-event log',                icon: Shield },
  { key: 'etf',         label: 'ETF',        title: 'ETF desk: published NAV, premium/discount vs the AP band, and basket',  icon: Layers },
  { key: 'marketstats', label: 'Market',     title: 'Historical market statistics over an arbitrary date range',             icon: LineChart },
  { key: 'posttrade',   label: 'Post-Trade', title: 'P&L attribution, maker/taker, fill history and slippage',               icon: BarChart2 },
  { key: 'controls',    label: 'Controls',   title: 'Spreads, inventory targets, flatten, kill switch and engine config',    icon: SlidersHorizontal },
  { key: 'settings',    label: 'Settings',   title: 'Appearance, identity, and operator preferences',                        icon: SettingsIcon },
];

export function Navigation({ activePage, setActivePage }: {
  activePage: PageKey;
  setActivePage: (p: PageKey) => void;
}) {
  // The rail scrolls horizontally below ~1000px, so the active tab can sit off
  // screen after a route change (or on first load from a deep link) and the
  // operator loses any indication of which workspace they are in.
  const activeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [activePage]);

  return (
    <nav className="nav-rail" aria-label="Workspaces">
      {NAV_ITEMS.map(item => {
        const Icon = item.icon;
        const active = activePage === item.key;
        return (
          <button
            key={item.key}
            ref={active ? activeRef : undefined}
            type="button"
            className={active ? 'active' : undefined}
            aria-current={active ? 'page' : undefined}
            title={item.title}
            onClick={() => setActivePage(item.key)}
          >
            <Icon size={16} aria-hidden="true" />
            <span>{item.label}</span>
          </button>
        );
      })}
    </nav>
  );
}
